import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CompanyApiUtils } from 'src/app/helpers/utils';
import { Subject } from 'rxjs';
import Utils from 'src/app/helpers/utils';

@Component({
  selector: 'app-dashboard-companies-details-groups',
  templateUrl: './dashboard-companies-details-groups.component.html',
  styleUrls: ['./dashboard-companies-details-groups.component.scss'],
})
export class DashboardCompaniesDetailsGroupsComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  private loadingInProgress = false;
  @Input() companyId: number = 0;
  @Input() activeTab: string = '';

  permissionObj = {
    canAdd: true,
    canFilter: true,
    canInactive: true,
  };

  canViewDetails = true;

  data: CompanyGroupValues[] = [];

  filterForm = this.formBuilder.group({
    group: [null, []],
    start_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    end_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
  });

  form = this.formBuilder.group({
    name: [null, [Validators.required]],
    department: [null, []],
  });

  departmentOptions: SelectItem[] = [];
  loading = true;
  /**
   * Padrão de Filtros:
   * - hasFilters só deve ser true APÓS aplicar os filtros (método filterData())
   * - NÃO atualizar hasFilters no valueChanges do filterForm
   * - hasFilters deve ser atualizado apenas quando:
   *   1. filterData() executar com sucesso (após a requisição)
   *   2. resetForm() for chamado (define como false)
   *   3. toggleFilterModal() quando há filtros salvos no storage
   */
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];
  showFilterModal = false;
  offset: number = 0;
  count: number = 0;
  profileOptions: SelectItem[] = [];

  showGroupModal = false;

  // Propriedades para o warning modal
  showStatusModal = false;
  selectedGroup: any = null;
  isActive: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    public signupService: SignupService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {}

  private dataLoaded = false;

  ngOnInit(): void {
    this.setPermissions();
    this.canViewDetails = this.authService.verifyPermission(
      'de_can_view_company_groups_details'
    );
    // Lazy loading: só carrega dados quando componente é inicializado (quando aba está ativa)
    if (!this.dataLoaded) {
      this.dataLoaded = true;
      this.getData();
      // this.getDepartments();
      // this.getProfiles();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Quando a aba mudar e não for mais 'groups', resetar os filtros
    if (
      changes['activeTab'] &&
      changes['activeTab'].previousValue === 'groups' &&
      changes['activeTab'].currentValue !== 'groups'
    ) {
      this.resetFiltersOnTabChange();
    }
    // Sempre que a aba 'groups' for ativada, recarregar dados sem filtros
    else if (changes['activeTab'] && changes['activeTab'].currentValue === 'groups') {
      // Verifica se o componente já foi inicializado
      if (this.dataLoaded) {
        // Resetar filtros e recarregar dados
        this.hasFilters = false;
        this.appliedFilters = [];
        this.filterForm.reset();
        this.getData();
      }
    }
  }

  resetFiltersOnTabChange() {
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadingInProgress = false;
  }

  //   async getDepartments(page: number = 1) {
  //     try {
  //       const res = await this.api.get({
  //         route: 'api/v2/company_department/',
  //         token: true,
  //         params: {
  //           page: page,
  //           company: this.companyId,
  //         },
  //       });

  //       this.departmentOptions = res.map((item) => ({
  //         label: item.department,
  //         value: item.id,
  //       }));
  //     } catch (error) {
  //       console.error(error);
  //       this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
  //     }
  //   }

  async getData(page: number = 1) {
    // Evitar múltiplas requisições simultâneas
    if (this.loadingInProgress) {
      return;
    }
    this.loadingInProgress = true;
    this.loading = true;
    try {
      const id = this.route.snapshot.paramMap.get('id');
      const filters: any = {};

      // Construir filtros a partir do formulário (se houver valores)
      const values = this.filterForm.value;
      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const response: any = await this.api.get({
        route: `api/v3/companies/${id}/`,
        token: true,
        params: {
          tab: 'groups',
          page: page,
          ...filters,
        },
      });

      // V3 sempre retorna paginação
      this.offset = response.showing_to || 0;
      this.count = response.count || 0;
      // Atribuir dados direto sem processamento (como outras páginas que funcionam)
      // O processamento será feito sob demanda no template através dos métodos displayDateTime() e getStatusDisplay()
      this.data = response.results || [];
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    } finally {
      this.loading = false;
      this.loadingInProgress = false;
    }
  }

  redirectToDetails(id: any) {
    const companyId = this.route.snapshot.params.id;
    this.router.navigateByUrl(
      `/app/permissions/companies/${companyId}/group/details/${id}`
    );
  }

  changePageData(page: number) {
    this.loading = true;
    this.getData(page);
  }

  async refreshData() {
    await this.getData();
  }

  resetForm() {
    this.loading = true;
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
    this.getData();
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    const fieldLabels: { [key: string]: string } = {
      group: 'Nome',
      start_date: 'Data inicial',
      end_date: 'Data final',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        let displayValue = value;

        // Tratamento especial para datas (já vem formatado do formulário)
        if (key === 'start_date' || key === 'end_date') {
          displayValue = value;
        }

        if (fieldLabels[key]) {
          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${String(displayValue)}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: String(displayValue),
          });
        }
      }
    });
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;

    if (this.showFilterModal) {
      // Se houver filtros aplicados, restaurar no formulário
      if (this.hasFilters && this.appliedFilters.length > 0) {
        // Os valores já estão no formulário, apenas garantir que as tags estão atualizadas
        this.generateFilterTags(this.filterForm.value);
      } else {
        this.hasFilters = false;
        this.appliedFilters = [];
      }
    }
  }

  async filterData() {
    // Verificar se o formulário é válido antes de processar
    if (this.filterForm.invalid) {
      // Marcar todos os campos como touched para mostrar erros
      Object.keys(this.filterForm.controls).forEach((key) => {
        this.filterForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.loading = true;

    try {
      const values = this.filterForm.value;
      const id = this.route.snapshot.paramMap.get('id');

      const filters: any = {};

      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const response: any = await this.api.get({
        route: `api/v3/companies/${id}/`,
        token: true,
        params: {
          tab: 'groups',
          page: 1,
          ...filters,
        },
      });

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(filters).length > 0;

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(values);

      // V3 sempre retorna paginação
      this.count = response.count || 0;
      this.offset = response.showing_to || 0;
      // Atribuir dados direto sem processamento (como outras páginas que funcionam)
      // O processamento será feito sob demanda no template através dos métodos displayDateTime() e getStatusDisplay()
      this.data = response.results || [];
      this.showFilterModal = false;
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  setPermissions() {
    const permissions = this.authService.mapPermissionsSlugs(
      'company_groups',
      this.permissionObj
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  renderProfiles(arr: any[]) {
    const labels = arr.map((item) => item.name);

    const displayLabels =
      labels.length > 3
        ? [...labels.slice(0, 3), `+ ${labels.length - 3} itens`]
        : labels;

    return displayLabels.join(', ');
  }

  //   async getProfiles(page: number = 1) {
  //     try {
  //       const id = this.route.snapshot.paramMap.get('id');

  //       const res = await this.api.get({
  //         route: `api/v2/company/${id}`,
  //         params: {
  //           tab: 'user_profile',
  //         },
  //         token: true,
  //       });

  //       this.profileOptions = res.map((item) => ({
  //         label: item.description,
  //         value: item.role_id,
  //       }));
  //     } catch (error) {
  //       console.error(error);
  //       this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
  //     }
  //     this.loading = false;
  //   }

  async addGroup() {
    try {
      const payload = {
        name: this.form.value.name,
        department: this.form.value.department,
        company: this.companyId,
      };

      const res = await this.api.post({
        route: `api/v2/user_group/`,
        token: true,
        body: payload,
      });

      this.getData();
      this.showGroupModal = false;
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao adicionar o grupo.');
    }
  }

  toggleStatus(id: number) {
    const findedGroup: any = this.data.find((item) => item.id === id);
    this.selectedGroup = findedGroup;
    // V3 usa 'active'/'inactive'
    this.isActive = findedGroup?.status === 'active';
    this.showStatusModal = true;
  }

  async confirmStatusChange() {
    try {
      const selectedGroup: any = this.selectedGroup;
      const res = await this.api.patch({
        route: `api/v2/user_group/${selectedGroup.id}/`,
        token: true,
        body: {
          // V3 usa 'active'/'inactive'
          status: selectedGroup.status === 'active' ? 'inactive' : 'active',
        },
      });

      this.loading = true;
      this.showStatusModal = false;
      this.getData();
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao alterar o status do grupo.'
      );
    }
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.selectedGroup = null;
    this.isActive = false;
  }

  getModalTitle(): string {
    return this.isActive ? 'Inativar grupo' : 'Ativar grupo';
  }

  getModalMessage(): string {
    return `Tem certeza de que deseja ${
      this.isActive ? 'inativar' : 'ativar'
    } o grupo "${
      this.selectedGroup?.name
    }"? Todas as permissões vinculadas a esse grupo também serão afetadas por essa operação.`;
  }

  getConfirmText(): string {
    return `Sim, ${this.isActive ? 'inativar' : 'ativar'} grupo`;
  }

  getStatusDisplay(status: string): string {
    return CompanyApiUtils.getStatusDisplay(status, 'general');
  }

  displayDateTime(dateStr: string) {
    if (!dateStr) return { date: '-', time: '' };

    try {
      // Se já vem no formato "DD/MM/YYYY HH:MM"
      if (dateStr.includes('/') && dateStr.includes(' ')) {
        const [datePart, timePart] = dateStr.split(' ');
        const [day, month, year] = datePart.split('/');
        const [hours, minutes] = timePart.split(':');

        return {
          date: `${day}/${month}/${year}`,
          time: `às ${hours}:${minutes}`,
        };
      }

      // Se for ISO datetime
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return {
        date: `${day}/${month}/${year}`,
        time: `às ${hours}:${minutes}`,
      };
    } catch (error) {
      return { date: dateStr || '-', time: '' };
    }
  }

  trackByItemId(index: number, item: any): any {
    return item?.id || index;
  }

  /**
   * Verifica se os filtros podem ser aplicados
   * Retorna false se houver campos inválidos ou se não houver nenhum filtro preenchido
   */
  get canApplyFilters(): boolean {
    // Verifica se o formulário é válido
    if (this.filterForm.invalid) {
      return false;
    }

    // Verifica se há pelo menos um filtro preenchido
    const values = this.filterForm.value;
    const hasAnyFilter = Object.values(values).some(
      (value) => value !== null && value !== '' && value !== undefined
    );

    return hasAnyFilter;
  }
}
